import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.properties': 'Properties',
    'nav.units': 'Units',
    'nav.tenants': 'Tenants',
    'nav.leases': 'Leases',
    'nav.payments': 'Payments',
    'nav.billing': 'Billing History',
    'nav.maintenance': 'Maintenance',
    'nav.reports': 'Reports',
    'nav.settings': 'Settings',
    'nav.subscription': 'Subscription',
    'nav.audit': 'Audit Logs',
    'nav.landlords': 'Landlords',
    'nav.organisations': 'Organisations',
    'nav.logout': 'Log Out',
    // Common
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.search': 'Search...',
    'common.noData': 'No data available',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.date': 'Date',
    'common.amount': 'Amount',
    'common.name': 'Name',
    'common.email': 'Email',
    'common.active': 'Active',
    'common.pending': 'Pending',
    'common.paid': 'Paid',
    'common.overdue': 'Overdue',
    'common.upgrade': 'Upgrade',
    'common.downgrade': 'Downgrade',
    'common.language': 'Language',
    'common.english': 'English',
    'common.french': 'French',
    'common.spanish': 'Spanish',
    'common.refresh': 'Refresh',
    'common.allTypes': 'All Types',
    'common.filter': 'Filter',
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome back,',
    'dashboard.subtitle': "Here's what's happening with your properties",
    'dashboard.properties': 'Properties',
    'dashboard.totalUnits': 'Total Units',
    'dashboard.occupied': 'occupied',
    'dashboard.activeTenants': 'Active Tenants',
    'dashboard.pendingRevenue': 'Pending Revenue',
    'dashboard.payments': 'payments',
    'dashboard.openTickets': 'Open Tickets',
    'dashboard.addProperty': 'Add Property',
    'dashboard.monthlyRevenue': 'Monthly Revenue',
    'dashboard.occupancyRate': 'Occupancy Rate',
    'dashboard.totalCollected': 'Total Collected',
    'dashboard.recentPayments': 'Recent Payments',
    'dashboard.maintenanceTickets': 'Maintenance Tickets',
    'dashboard.noPayments': 'No payments yet',
    'dashboard.noTickets': 'No maintenance tickets',
    'dashboard.viewAll': 'View All',
    'dashboard.paymentStatus': 'Payment Status',
    'dashboard.vacantUnits': 'Vacant Units',
    'dashboard.currentRate': 'Current Rate',
    'dashboard.satisfaction': 'Tenant Satisfaction',
    'dashboard.overallRating': 'Overall Rating',
    'dashboard.totalResponses': 'Total Responses',
    'dashboard.byProperty': 'By Property',
    'dashboard.noRatings': 'No ratings yet',
    'dashboard.activeLeases': 'Active Leases',
    // Properties
    'properties.title': 'Properties',
    'properties.subtitle': 'Manage your property portfolio',
    'properties.addProperty': 'Add Property',
    'properties.addFirst': 'Add Your First Property',
    'properties.noProperties': 'No Properties Yet',
    'properties.noPropertiesDesc': 'Add your first property to get started',
    'properties.editProperty': 'Edit Property',
    'properties.addNewProperty': 'Add New Property',
    'properties.propertyName': 'Property Name',
    'properties.address': 'Address',
    'properties.city': 'City',
    'properties.state': 'State',
    'properties.zip': 'ZIP',
    'properties.propertyType': 'Property Type',
    'properties.buildingType': 'Building Type',
    'properties.description': 'Description',
    'properties.residential': 'Residential',
    'properties.commercial': 'Commercial',
    'properties.mixed': 'Mixed Use',
    'properties.updateProperty': 'Update Property',
    'properties.createProperty': 'Create Property',
    // Units
    'units.title': 'Units',
    'units.subtitle': 'Manage individual rental units',
    'units.addUnit': 'Add Unit',
    'units.addFirst': 'Add Your First Unit',
    'units.noUnits': 'No Units Yet',
    'units.noUnitsDesc': 'Add units to your properties to get started',
    'units.addPropertyFirst': 'Add a Property First',
    'units.addPropertyFirstDesc': 'You need to create a property before adding units',
    'units.editUnit': 'Edit Unit',
    'units.addNewUnit': 'Add New Unit',
    'units.allProperties': 'All Properties',
    'units.property': 'Property',
    'units.unitNumber': 'Unit Number',
    'units.sqft': 'Sq Ft',
    'units.bedrooms': 'Bedrooms',
    'units.bathrooms': 'Bathrooms',
    'units.monthlyRent': 'Monthly Rent ($)',
    'units.deposit': 'Deposit ($)',
    'units.updateUnit': 'Update Unit',
    'units.createUnit': 'Create Unit',
    // Tenants
    'tenants.title': 'Tenants',
    'tenants.subtitle': 'Manage your tenants and invitations',
    'tenants.inviteTenant': 'Invite Tenant',
    'tenants.inviteFirst': 'Invite Your First Tenant',
    'tenants.noTenants': 'No Tenants Yet',
    'tenants.noTenantsDesc': 'Invite tenants to your properties to get started',
    'tenants.inviteNew': 'Invite New Tenant',
    'tenants.tenantName': 'Tenant Name',
    'tenants.emailAddress': 'Email Address',
    'tenants.phone': 'Phone (Optional)',
    'tenants.assignUnit': 'Assign to Unit (Optional)',
    'tenants.sendInvitation': 'Send Invitation',
    'tenants.pendingInvitations': 'Pending Invitations',
    'tenants.invitationHistory': 'Invitation History',
    'tenants.docs': 'Docs',
    'tenants.leaseDocuments': 'Lease Documents',
    'tenants.paymentReceipts': 'Payment Receipts',
    // Leases
    'leases.title': 'Leases',
    'leases.subtitle': 'Manage lease agreements',
    'leases.createLease': 'Create Lease',
    'leases.noLeases': 'No Leases Yet',
    'leases.noLeasesDesc': 'Create a lease to assign tenants to units',
    // Payments
    'payments.title': 'Payments',
    'payments.subtitle': 'Track rent payments and collections',
    'payments.pendingRevenue': 'Pending Revenue',
    'payments.collected': 'Collected',
    'payments.totalRequests': 'Total Requests',
    'payments.createPayment': 'Create Payment Request',
    'payments.generateMonthly': 'Generate Monthly',
    'payments.generating': 'Generating...',
    'payments.noPayments': 'No Payment Requests',
    'payments.noPaymentsDesc': 'Create payment requests for your tenants',
    'payments.createFirst': 'Create First Payment Request',
    'payments.needLeases': 'You need active leases to create payment requests',
    'payments.lateFee': 'late fee',
    // Maintenance
    'maintenance.title': 'Maintenance',
    'maintenance.subtitle': 'Track and resolve maintenance requests',
    'maintenance.createRequest': 'Create Request',
    'maintenance.noRequests': 'No Maintenance Requests',
    // Reports
    'reports.title': 'Reports',
    'reports.subtitle': 'Financial and operational reports',
    // Subscription
    'subscription.title': 'Subscription',
    'subscription.currentPlan': 'Current Plan',
    'subscription.processing': 'Processing your payment...',
    'subscription.planChanges': 'Plan Changes',
    'subscription.planChangesDesc': "All plan changes are processed through Stripe's secure checkout. You'll be redirected to enter or confirm your payment details. Your new plan starts immediately after payment is complete.",
    'subscription.whyUpgrade': 'Why Upgrade?',
    'subscription.moreProperties': 'More Properties & Units',
    'subscription.morePropertiesDesc': 'Scale your portfolio with higher limits on properties and units',
    'subscription.advancedFeatures': 'Advanced Features',
    'subscription.advancedFeaturesDesc': 'Access powerful reporting, analytics, and automation tools',
    'subscription.prioritySupport': 'Priority Support',
    'subscription.prioritySupportDesc': 'Get dedicated support for your business needs',
    'subscription.confirmChange': 'Confirm Plan Change',
    'subscription.current': 'Current',
    'subscription.new': 'New',
    'subscription.paymentRequired': 'Payment Required',
    'subscription.proceedPayment': 'Proceed to Payment',
    'subscription.redirecting': 'Redirecting to Stripe...',
    // Settings
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your account, payments, and notification preferences',
    'settings.paymentAccount': 'Payment Account',
    'settings.organisation': 'Organisation',
    'settings.account': 'Account',
    'settings.notifications': 'Notifications',
    'settings.weeklyDigest': 'Weekly Analytics Digest',
    'settings.weeklyDigestDesc': 'Receive a weekly email every Monday with your portfolio summary: MRR, active leases, occupancy rate, overdue payments, and open maintenance tickets.',
    'settings.connectStripe': 'Connect Stripe Account',
    'settings.stripeConnected': 'Your Stripe account is connected',
    'settings.stripePending': 'Account verification in progress',
    'settings.stripeNotConnected': 'Connect your Stripe account to receive rent payments',
    'settings.language': 'Language Preference',
    'settings.languageDesc': 'Choose your preferred language for the interface',
    // Billing
    'billing.title': 'Billing History',
    'billing.subtitle': 'View your past invoices and download receipts',
    'billing.invoice': 'Invoice',
    'billing.period': 'Period',
    'billing.viewInvoice': 'View Invoice',
    'billing.downloadPdf': 'Download PDF',
    'billing.noInvoices': 'No invoices yet',
    'billing.noInvoicesDesc': 'Your billing history will appear here after your first payment',
    // Audit
    'audit.title': 'Audit Logs',
    'audit.subtitle': 'Track all actions performed in your organisation',
    'audit.action': 'Action',
    'audit.user': 'User',
    'audit.resource': 'Resource',
    'audit.details': 'Details',
    'audit.timestamp': 'Timestamp',
    'audit.noLogs': 'No audit logs yet',
    'audit.noLogsDesc': 'Activity logs will appear here as actions are performed',
    // Plan limits
    'limits.propertyReached': 'Property limit reached',
    'limits.unitReached': 'Unit limit reached',
    'limits.upgradePrompt': 'Please upgrade your subscription',
    // Admin
    'admin.title': 'Admin Dashboard',
    'admin.landlords': 'Landlords',
    'admin.organisations': 'Organisations',
    // Tenant portal
    'tenant.dashboard': 'Dashboard',
    'tenant.lease': 'My Lease',
    'tenant.payments': 'Payments',
    'tenant.maintenance': 'Maintenance',
    'tenant.documents': 'Documents',
    'tenant.notifications': 'Notifications',
  },
  fr: {
    // Navigation
    'nav.dashboard': 'Tableau de bord',
    'nav.properties': 'Propriétés',
    'nav.units': 'Unités',
    'nav.tenants': 'Locataires',
    'nav.leases': 'Baux',
    'nav.payments': 'Paiements',
    'nav.billing': 'Historique de facturation',
    'nav.maintenance': 'Maintenance',
    'nav.reports': 'Rapports',
    'nav.settings': 'Paramètres',
    'nav.subscription': 'Abonnement',
    'nav.audit': "Journal d'audit",
    'nav.landlords': 'Propriétaires',
    'nav.organisations': 'Organisations',
    'nav.logout': 'Déconnexion',
    // Common
    'common.loading': 'Chargement...',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.create': 'Créer',
    'common.search': 'Rechercher...',
    'common.noData': 'Aucune donnée disponible',
    'common.actions': 'Actions',
    'common.status': 'Statut',
    'common.date': 'Date',
    'common.amount': 'Montant',
    'common.name': 'Nom',
    'common.email': 'E-mail',
    'common.active': 'Actif',
    'common.pending': 'En attente',
    'common.paid': 'Payé',
    'common.overdue': 'En retard',
    'common.upgrade': 'Mettre à niveau',
    'common.downgrade': 'Rétrograder',
    'common.language': 'Langue',
    'common.english': 'Anglais',
    'common.french': 'Français',
    'common.spanish': 'Espagnol',
    'common.refresh': 'Actualiser',
    'common.allTypes': 'Tous les types',
    'common.filter': 'Filtrer',
    // Dashboard
    'dashboard.title': 'Tableau de bord',
    'dashboard.welcome': 'Bon retour,',
    'dashboard.subtitle': "Voici ce qui se passe avec vos propriétés",
    'dashboard.properties': 'Propriétés',
    'dashboard.totalUnits': 'Total unités',
    'dashboard.occupied': 'occupé(s)',
    'dashboard.activeTenants': 'Locataires actifs',
    'dashboard.pendingRevenue': 'Revenus en attente',
    'dashboard.payments': 'paiements',
    'dashboard.openTickets': 'Tickets ouverts',
    'dashboard.addProperty': 'Ajouter une propriété',
    'dashboard.monthlyRevenue': 'Revenus mensuels',
    'dashboard.occupancyRate': "Taux d'occupation",
    'dashboard.totalCollected': 'Total encaissé',
    'dashboard.recentPayments': 'Paiements récents',
    'dashboard.maintenanceTickets': 'Tickets de maintenance',
    'dashboard.noPayments': 'Aucun paiement',
    'dashboard.noTickets': 'Aucun ticket de maintenance',
    'dashboard.viewAll': 'Voir tout',
    'dashboard.paymentStatus': 'Statut des paiements',
    'dashboard.vacantUnits': 'Unités vacantes',
    'dashboard.currentRate': 'Taux actuel',
    'dashboard.satisfaction': 'Satisfaction des locataires',
    'dashboard.overallRating': 'Note globale',
    'dashboard.totalResponses': 'Total des réponses',
    'dashboard.byProperty': 'Par propriété',
    'dashboard.noRatings': 'Aucune évaluation',
    'dashboard.activeLeases': 'Baux actifs',
    // Properties
    'properties.title': 'Propriétés',
    'properties.subtitle': 'Gérez votre portefeuille immobilier',
    'properties.addProperty': 'Ajouter une propriété',
    'properties.addFirst': 'Ajoutez votre première propriété',
    'properties.noProperties': 'Aucune propriété',
    'properties.noPropertiesDesc': 'Ajoutez votre première propriété pour commencer',
    'properties.editProperty': 'Modifier la propriété',
    'properties.addNewProperty': 'Nouvelle propriété',
    'properties.propertyName': 'Nom de la propriété',
    'properties.address': 'Adresse',
    'properties.city': 'Ville',
    'properties.state': 'Province',
    'properties.zip': 'Code postal',
    'properties.propertyType': 'Type de propriété',
    'properties.buildingType': "Type d'immeuble",
    'properties.description': 'Description',
    'properties.residential': 'Résidentiel',
    'properties.commercial': 'Commercial',
    'properties.mixed': 'Usage mixte',
    'properties.updateProperty': 'Mettre à jour',
    'properties.createProperty': 'Créer la propriété',
    // Units
    'units.title': 'Unités',
    'units.subtitle': 'Gérez les unités de location individuelles',
    'units.addUnit': 'Ajouter une unité',
    'units.addFirst': 'Ajoutez votre première unité',
    'units.noUnits': 'Aucune unité',
    'units.noUnitsDesc': 'Ajoutez des unités à vos propriétés pour commencer',
    'units.addPropertyFirst': "D'abord, ajoutez une propriété",
    'units.addPropertyFirstDesc': "Vous devez créer une propriété avant d'ajouter des unités",
    'units.editUnit': "Modifier l'unité",
    'units.addNewUnit': 'Nouvelle unité',
    'units.allProperties': 'Toutes les propriétés',
    'units.property': 'Propriété',
    'units.unitNumber': "Numéro d'unité",
    'units.sqft': 'Pi²',
    'units.bedrooms': 'Chambres',
    'units.bathrooms': 'Salles de bain',
    'units.monthlyRent': 'Loyer mensuel ($)',
    'units.deposit': 'Dépôt ($)',
    'units.updateUnit': 'Mettre à jour',
    'units.createUnit': "Créer l'unité",
    // Tenants
    'tenants.title': 'Locataires',
    'tenants.subtitle': 'Gérez vos locataires et invitations',
    'tenants.inviteTenant': 'Inviter un locataire',
    'tenants.inviteFirst': 'Invitez votre premier locataire',
    'tenants.noTenants': 'Aucun locataire',
    'tenants.noTenantsDesc': 'Invitez des locataires pour commencer',
    'tenants.inviteNew': 'Inviter un nouveau locataire',
    'tenants.tenantName': 'Nom du locataire',
    'tenants.emailAddress': 'Adresse e-mail',
    'tenants.phone': 'Téléphone (Optionnel)',
    'tenants.assignUnit': 'Assigner à une unité (Optionnel)',
    'tenants.sendInvitation': "Envoyer l'invitation",
    'tenants.pendingInvitations': 'Invitations en attente',
    'tenants.invitationHistory': 'Historique des invitations',
    'tenants.docs': 'Docs',
    'tenants.leaseDocuments': 'Documents de bail',
    'tenants.paymentReceipts': 'Reçus de paiement',
    // Leases
    'leases.title': 'Baux',
    'leases.subtitle': 'Gérez les contrats de bail',
    'leases.createLease': 'Créer un bail',
    'leases.noLeases': 'Aucun bail',
    'leases.noLeasesDesc': 'Créez un bail pour assigner des locataires aux unités',
    // Payments
    'payments.title': 'Paiements',
    'payments.subtitle': 'Suivez les paiements de loyer et les encaissements',
    'payments.pendingRevenue': 'Revenus en attente',
    'payments.collected': 'Encaissé',
    'payments.totalRequests': 'Total des demandes',
    'payments.createPayment': 'Créer une demande de paiement',
    'payments.generateMonthly': 'Générer mensuellement',
    'payments.generating': 'Génération...',
    'payments.noPayments': 'Aucune demande de paiement',
    'payments.noPaymentsDesc': 'Créez des demandes de paiement pour vos locataires',
    'payments.createFirst': 'Créer la première demande',
    'payments.needLeases': 'Vous avez besoin de baux actifs pour créer des demandes de paiement',
    'payments.lateFee': 'frais de retard',
    // Maintenance
    'maintenance.title': 'Maintenance',
    'maintenance.subtitle': 'Suivez et résolvez les demandes de maintenance',
    'maintenance.createRequest': 'Créer une demande',
    'maintenance.noRequests': 'Aucune demande de maintenance',
    // Reports
    'reports.title': 'Rapports',
    'reports.subtitle': 'Rapports financiers et opérationnels',
    // Subscription
    'subscription.title': 'Abonnement',
    'subscription.currentPlan': 'Plan actuel',
    'subscription.processing': 'Traitement de votre paiement...',
    'subscription.planChanges': 'Changement de plan',
    'subscription.planChangesDesc': "Tous les changements de plan passent par le paiement sécurisé Stripe. Vous serez redirigé pour entrer ou confirmer vos informations de paiement. Votre nouveau plan commence immédiatement après le paiement.",
    'subscription.whyUpgrade': 'Pourquoi mettre à niveau ?',
    'subscription.moreProperties': 'Plus de propriétés et unités',
    'subscription.morePropertiesDesc': 'Développez votre portefeuille avec des limites plus élevées',
    'subscription.advancedFeatures': 'Fonctionnalités avancées',
    'subscription.advancedFeaturesDesc': "Accédez à des outils puissants d'analyse et d'automatisation",
    'subscription.prioritySupport': 'Support prioritaire',
    'subscription.prioritySupportDesc': 'Obtenez un support dédié pour vos besoins',
    'subscription.confirmChange': 'Confirmer le changement de plan',
    'subscription.current': 'Actuel',
    'subscription.new': 'Nouveau',
    'subscription.paymentRequired': 'Paiement requis',
    'subscription.proceedPayment': 'Procéder au paiement',
    'subscription.redirecting': 'Redirection vers Stripe...',
    // Settings
    'settings.title': 'Paramètres',
    'settings.subtitle': 'Gérez votre compte, paiements et préférences de notification',
    'settings.paymentAccount': 'Compte de paiement',
    'settings.organisation': 'Organisation',
    'settings.account': 'Compte',
    'settings.notifications': 'Notifications',
    'settings.weeklyDigest': 'Résumé hebdomadaire',
    'settings.weeklyDigestDesc': 'Recevez un e-mail hebdomadaire chaque lundi avec le résumé de votre portefeuille.',
    'settings.connectStripe': 'Connecter Stripe',
    'settings.stripeConnected': 'Votre compte Stripe est connecté',
    'settings.stripePending': 'Vérification du compte en cours',
    'settings.stripeNotConnected': 'Connectez votre compte Stripe pour recevoir les loyers',
    'settings.language': 'Préférence de langue',
    'settings.languageDesc': "Choisissez votre langue préférée pour l'interface",
    // Billing
    'billing.title': 'Historique de facturation',
    'billing.subtitle': 'Consultez vos factures passées et téléchargez les reçus',
    'billing.invoice': 'Facture',
    'billing.period': 'Période',
    'billing.viewInvoice': 'Voir la facture',
    'billing.downloadPdf': 'Télécharger PDF',
    'billing.noInvoices': 'Aucune facture',
    'billing.noInvoicesDesc': 'Votre historique de facturation apparaîtra ici après votre premier paiement',
    // Audit
    'audit.title': "Journal d'audit",
    'audit.subtitle': 'Suivez toutes les actions effectuées dans votre organisation',
    'audit.action': 'Action',
    'audit.user': 'Utilisateur',
    'audit.resource': 'Ressource',
    'audit.details': 'Détails',
    'audit.timestamp': 'Horodatage',
    'audit.noLogs': "Aucun journal d'audit",
    'audit.noLogsDesc': "Les journaux d'activité apparaîtront ici au fur et à mesure des actions",
    // Plan limits
    'limits.propertyReached': 'Limite de propriétés atteinte',
    'limits.unitReached': "Limite d'unités atteinte",
    'limits.upgradePrompt': 'Veuillez mettre à niveau votre abonnement',
    // Admin
    'admin.title': "Tableau de bord d'administration",
    'admin.landlords': 'Propriétaires',
    'admin.organisations': 'Organisations',
    // Tenant portal
    'tenant.dashboard': 'Tableau de bord',
    'tenant.lease': 'Mon bail',
    'tenant.payments': 'Paiements',
    'tenant.maintenance': 'Maintenance',
    'tenant.documents': 'Documents',
    'tenant.notifications': 'Notifications',
  },
  es: {
    // Navigation
    'nav.dashboard': 'Panel de control',
    'nav.properties': 'Propiedades',
    'nav.units': 'Unidades',
    'nav.tenants': 'Inquilinos',
    'nav.leases': 'Contratos',
    'nav.payments': 'Pagos',
    'nav.billing': 'Historial de facturación',
    'nav.maintenance': 'Mantenimiento',
    'nav.reports': 'Informes',
    'nav.settings': 'Configuración',
    'nav.subscription': 'Suscripción',
    'nav.audit': 'Registro de auditoría',
    'nav.landlords': 'Propietarios',
    'nav.organisations': 'Organizaciones',
    'nav.logout': 'Cerrar sesión',
    // Common
    'common.loading': 'Cargando...',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.create': 'Crear',
    'common.search': 'Buscar...',
    'common.noData': 'No hay datos disponibles',
    'common.actions': 'Acciones',
    'common.status': 'Estado',
    'common.date': 'Fecha',
    'common.amount': 'Monto',
    'common.name': 'Nombre',
    'common.email': 'Correo electrónico',
    'common.active': 'Activo',
    'common.pending': 'Pendiente',
    'common.paid': 'Pagado',
    'common.overdue': 'Vencido',
    'common.upgrade': 'Mejorar plan',
    'common.downgrade': 'Reducir plan',
    'common.language': 'Idioma',
    'common.english': 'Inglés',
    'common.french': 'Francés',
    'common.spanish': 'Español',
    'common.refresh': 'Actualizar',
    'common.allTypes': 'Todos los tipos',
    'common.filter': 'Filtrar',
    // Dashboard
    'dashboard.title': 'Panel de control',
    'dashboard.welcome': 'Bienvenido de nuevo,',
    'dashboard.subtitle': 'Esto es lo que está pasando con tus propiedades',
    'dashboard.properties': 'Propiedades',
    'dashboard.totalUnits': 'Total de unidades',
    'dashboard.occupied': 'ocupada(s)',
    'dashboard.activeTenants': 'Inquilinos activos',
    'dashboard.pendingRevenue': 'Ingresos pendientes',
    'dashboard.payments': 'pagos',
    'dashboard.openTickets': 'Tickets abiertos',
    'dashboard.addProperty': 'Agregar propiedad',
    'dashboard.monthlyRevenue': 'Ingresos mensuales',
    'dashboard.occupancyRate': 'Tasa de ocupación',
    'dashboard.totalCollected': 'Total cobrado',
    'dashboard.recentPayments': 'Pagos recientes',
    'dashboard.maintenanceTickets': 'Tickets de mantenimiento',
    'dashboard.noPayments': 'Sin pagos aún',
    'dashboard.noTickets': 'Sin tickets de mantenimiento',
    'dashboard.viewAll': 'Ver todo',
    'dashboard.paymentStatus': 'Estado de pagos',
    'dashboard.vacantUnits': 'Unidades vacantes',
    'dashboard.currentRate': 'Tasa actual',
    'dashboard.satisfaction': 'Satisfacción de inquilinos',
    'dashboard.overallRating': 'Calificación general',
    'dashboard.totalResponses': 'Total de respuestas',
    'dashboard.byProperty': 'Por propiedad',
    'dashboard.noRatings': 'Sin calificaciones',
    'dashboard.activeLeases': 'Contratos activos',
    // Properties
    'properties.title': 'Propiedades',
    'properties.subtitle': 'Gestiona tu portafolio inmobiliario',
    'properties.addProperty': 'Agregar propiedad',
    'properties.addFirst': 'Agrega tu primera propiedad',
    'properties.noProperties': 'Sin propiedades',
    'properties.noPropertiesDesc': 'Agrega tu primera propiedad para comenzar',
    'properties.editProperty': 'Editar propiedad',
    'properties.addNewProperty': 'Nueva propiedad',
    'properties.propertyName': 'Nombre de la propiedad',
    'properties.address': 'Dirección',
    'properties.city': 'Ciudad',
    'properties.state': 'Estado',
    'properties.zip': 'Código postal',
    'properties.propertyType': 'Tipo de propiedad',
    'properties.buildingType': 'Tipo de edificio',
    'properties.description': 'Descripción',
    'properties.residential': 'Residencial',
    'properties.commercial': 'Comercial',
    'properties.mixed': 'Uso mixto',
    'properties.updateProperty': 'Actualizar',
    'properties.createProperty': 'Crear propiedad',
    // Units
    'units.title': 'Unidades',
    'units.subtitle': 'Gestiona las unidades de alquiler individuales',
    'units.addUnit': 'Agregar unidad',
    'units.addFirst': 'Agrega tu primera unidad',
    'units.noUnits': 'Sin unidades',
    'units.noUnitsDesc': 'Agrega unidades a tus propiedades para comenzar',
    'units.addPropertyFirst': 'Primero, agrega una propiedad',
    'units.addPropertyFirstDesc': 'Necesitas crear una propiedad antes de agregar unidades',
    'units.editUnit': 'Editar unidad',
    'units.addNewUnit': 'Nueva unidad',
    'units.allProperties': 'Todas las propiedades',
    'units.property': 'Propiedad',
    'units.unitNumber': 'Número de unidad',
    'units.sqft': 'Pies²',
    'units.bedrooms': 'Habitaciones',
    'units.bathrooms': 'Baños',
    'units.monthlyRent': 'Alquiler mensual ($)',
    'units.deposit': 'Depósito ($)',
    'units.updateUnit': 'Actualizar',
    'units.createUnit': 'Crear unidad',
    // Tenants
    'tenants.title': 'Inquilinos',
    'tenants.subtitle': 'Gestiona tus inquilinos e invitaciones',
    'tenants.inviteTenant': 'Invitar inquilino',
    'tenants.inviteFirst': 'Invita a tu primer inquilino',
    'tenants.noTenants': 'Sin inquilinos',
    'tenants.noTenantsDesc': 'Invita inquilinos a tus propiedades para comenzar',
    'tenants.inviteNew': 'Invitar nuevo inquilino',
    'tenants.tenantName': 'Nombre del inquilino',
    'tenants.emailAddress': 'Correo electrónico',
    'tenants.phone': 'Teléfono (Opcional)',
    'tenants.assignUnit': 'Asignar a unidad (Opcional)',
    'tenants.sendInvitation': 'Enviar invitación',
    'tenants.pendingInvitations': 'Invitaciones pendientes',
    'tenants.invitationHistory': 'Historial de invitaciones',
    'tenants.docs': 'Docs',
    'tenants.leaseDocuments': 'Documentos de contrato',
    'tenants.paymentReceipts': 'Recibos de pago',
    // Leases
    'leases.title': 'Contratos',
    'leases.subtitle': 'Gestiona los contratos de arrendamiento',
    'leases.createLease': 'Crear contrato',
    'leases.noLeases': 'Sin contratos',
    'leases.noLeasesDesc': 'Crea un contrato para asignar inquilinos a unidades',
    // Payments
    'payments.title': 'Pagos',
    'payments.subtitle': 'Rastrea pagos de alquiler y cobros',
    'payments.pendingRevenue': 'Ingresos pendientes',
    'payments.collected': 'Cobrado',
    'payments.totalRequests': 'Total de solicitudes',
    'payments.createPayment': 'Crear solicitud de pago',
    'payments.generateMonthly': 'Generar mensual',
    'payments.generating': 'Generando...',
    'payments.noPayments': 'Sin solicitudes de pago',
    'payments.noPaymentsDesc': 'Crea solicitudes de pago para tus inquilinos',
    'payments.createFirst': 'Crear primera solicitud',
    'payments.needLeases': 'Necesitas contratos activos para crear solicitudes de pago',
    'payments.lateFee': 'cargo por retraso',
    // Maintenance
    'maintenance.title': 'Mantenimiento',
    'maintenance.subtitle': 'Rastrea y resuelve solicitudes de mantenimiento',
    'maintenance.createRequest': 'Crear solicitud',
    'maintenance.noRequests': 'Sin solicitudes de mantenimiento',
    // Reports
    'reports.title': 'Informes',
    'reports.subtitle': 'Informes financieros y operacionales',
    // Subscription
    'subscription.title': 'Suscripción',
    'subscription.currentPlan': 'Plan actual',
    'subscription.processing': 'Procesando tu pago...',
    'subscription.planChanges': 'Cambios de plan',
    'subscription.planChangesDesc': 'Todos los cambios de plan se procesan a través del pago seguro de Stripe. Serás redirigido para ingresar o confirmar tus datos de pago. Tu nuevo plan comienza inmediatamente después del pago.',
    'subscription.whyUpgrade': '¿Por qué mejorar?',
    'subscription.moreProperties': 'Más propiedades y unidades',
    'subscription.morePropertiesDesc': 'Escala tu portafolio con límites más altos',
    'subscription.advancedFeatures': 'Funciones avanzadas',
    'subscription.advancedFeaturesDesc': 'Accede a herramientas de análisis y automatización',
    'subscription.prioritySupport': 'Soporte prioritario',
    'subscription.prioritySupportDesc': 'Obtén soporte dedicado para tus necesidades',
    'subscription.confirmChange': 'Confirmar cambio de plan',
    'subscription.current': 'Actual',
    'subscription.new': 'Nuevo',
    'subscription.paymentRequired': 'Pago requerido',
    'subscription.proceedPayment': 'Proceder al pago',
    'subscription.redirecting': 'Redirigiendo a Stripe...',
    // Settings
    'settings.title': 'Configuración',
    'settings.subtitle': 'Gestiona tu cuenta, pagos y preferencias de notificación',
    'settings.paymentAccount': 'Cuenta de pago',
    'settings.organisation': 'Organización',
    'settings.account': 'Cuenta',
    'settings.notifications': 'Notificaciones',
    'settings.weeklyDigest': 'Resumen semanal',
    'settings.weeklyDigestDesc': 'Recibe un correo semanal cada lunes con el resumen de tu portafolio.',
    'settings.connectStripe': 'Conectar Stripe',
    'settings.stripeConnected': 'Tu cuenta de Stripe está conectada',
    'settings.stripePending': 'Verificación de cuenta en progreso',
    'settings.stripeNotConnected': 'Conecta tu cuenta de Stripe para recibir pagos de alquiler',
    'settings.language': 'Preferencia de idioma',
    'settings.languageDesc': 'Elige tu idioma preferido para la interfaz',
    // Billing
    'billing.title': 'Historial de facturación',
    'billing.subtitle': 'Vea sus facturas anteriores y descargue recibos',
    'billing.invoice': 'Factura',
    'billing.period': 'Período',
    'billing.viewInvoice': 'Ver factura',
    'billing.downloadPdf': 'Descargar PDF',
    'billing.noInvoices': 'Sin facturas',
    'billing.noInvoicesDesc': 'Su historial de facturación aparecerá aquí después de su primer pago',
    // Audit
    'audit.title': 'Registro de auditoría',
    'audit.subtitle': 'Rastree todas las acciones realizadas en su organización',
    'audit.action': 'Acción',
    'audit.user': 'Usuario',
    'audit.resource': 'Recurso',
    'audit.details': 'Detalles',
    'audit.timestamp': 'Marca de tiempo',
    'audit.noLogs': 'Sin registros de auditoría',
    'audit.noLogsDesc': 'Los registros de actividad aparecerán aquí a medida que se realicen acciones',
    // Plan limits
    'limits.propertyReached': 'Límite de propiedades alcanzado',
    'limits.unitReached': 'Límite de unidades alcanzado',
    'limits.upgradePrompt': 'Por favor, mejore su suscripción',
    // Admin
    'admin.title': 'Panel de administración',
    'admin.landlords': 'Propietarios',
    'admin.organisations': 'Organizaciones',
    // Tenant portal
    'tenant.dashboard': 'Panel de control',
    'tenant.lease': 'Mi contrato',
    'tenant.payments': 'Pagos',
    'tenant.maintenance': 'Mantenimiento',
    'tenant.documents': 'Documentos',
    'tenant.notifications': 'Notificaciones',
  },
};

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [locale, setLocale] = useState(() => {
    return localStorage.getItem('pv_locale') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('pv_locale', locale);
  }, [locale]);

  const t = (key) => {
    return translations[locale]?.[key] || translations.en[key] || key;
  };

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};
